<script setup>
import { computed } from 'vue'
import Navigation from '~/components/Navigation.vue'
import { useNavigationStore } from '~/store/navigation.js'
import { Close } from '@element-plus/icons-vue'
import { useQuotesStore } from '~/store/quotes.js'
import { useDayjs } from '#dayjs'
import SentryFeedbackButtonHorizontal from './SentryFeedbackButtonHorizontal.vue'
import { ElIcon } from 'element-plus'
import svgWhatsapp from '~/assets/images/icons/whatsapp.svg'
import { getAvaliableQuoteGiverCodes } from '~/utils/common/helpers.js'

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
})
const navigationStore = useNavigationStore()
const quoteLeadsStore = useQuotesStore()

const isExpanded = computed(() => navigationStore.isExpanded)

const dayjs = useDayjs()

const logo = computed(() => (isExpanded.value ? '/images/avrillo-logo.png' : '/images/favicon.png'))

const mobileOpen = computed(() => navigationStore.mobileOpen)
// eslint-disable-next-line vue/return-in-computed-property
const getPendingSoldQuotesBadge = computed(() => {
  return `
  <div class="relative inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-red-500 border-2 border-white rounded-full top-0 -end-1 dark:border-gray-900">${quoteLeadsStore.quoteStatsRequests?.sold_pending ?? 0}</div>
  `
})
// eslint-disable-next-line vue/return-in-computed-property
const getPendingPreSoldQuotesBadge = computed(() => {
  return `
  <div class="relative inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-red-500 border-2 border-white rounded-full top-0 -end-1 dark:border-gray-900">${quoteLeadsStore.quoteStatsRequests?.presold_pending ?? 0}</div>
  `
})

// eslint-disable-next-line vue/return-in-computed-property
const getInDraftSoldQuotesBadge = computed(() => {
  return `
  <div class="relative inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-amber-500 border-2 border-white rounded-full -top-0.5 -end-1 dark:border-gray-900">${quoteLeadsStore.quoteStatsRequests?.sold_draft ?? 0}</div>
  `
})
// eslint-disable-next-line vue/return-in-computed-property
const getInDraftPreSoldQuotesBadge = computed(() => {
  return `
  <div class="relative inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-amber-500 border-2 border-white rounded-full -top-0.5 -end-1 dark:border-gray-900">${quoteLeadsStore.quoteStatsRequests?.presold_draft ?? 0}</div>
  `
})

const startOfCurrentMonth = dayjs().startOf('month')

const defaultCallLogFilters = computed(() => {
  return `?page=1&perPage=30&filters[sold_or_not_code][values][0]=S&filters[sold_or_not_code][filterType]=set&filters[sold_or_not_code][columnKey]=sold_or_not_code`
})

const startOfMonth = dayjs().startOf('month')
const today = dayjs()

const defaultFiltersStats = computed(() => {
  return `?page=1&perPage=30&filters[sold_or_not_code][values][0]=S&filters[sold_or_not_code][filterType]=set&filters[sold_or_not_code][columnKey]=sold_or_not_code&start_date=${startOfMonth.format('YYYY-MM-DD')}&end_date=${today.format('YYYY-MM-DD')}`
})

const defaultSlimFiltersListQuotes = computed(() => {
  return `?page=1&perPage=100&sort[quote_date_with_created]=desc`
})

const defaultFiltersListQuotes = computed(() => {
  return `?page=1&perPage=100&sort[quote_date_with_created]=desc`
})

const defaultFiltersDeadTomorrowQuotes = computed(() => {
  return `?page=1&perPage=30&filters[dead_tomorrow_pending]=true`
})

const defaultFiltersDeadQuotes = computed(() => {
  return `?page=1&perPage=30&filters[status]=Dead`
})

const defaultQuoteGivers = computed(() => {
  return getAvaliableQuoteGiverCodes.value.map((code, i) => `&filters[quote_giver_code][values][${i}]=${code}`).join('') + '&'
})

const defaultCallStatusFilters = computed(() => {
  return `?page=1&perPage=30&filters[sold_or_not_code][values][0]=S&filters[sold_or_not_code][filterType]=set&filters[sold_or_not_code][columnKey]=sold_or_not_code${defaultQuoteGivers.value}filters[quote_giver_code][filterType]=set&filters[quote_giver_code][columnKey]=quote_giver_code&filters[status][values][0]=Pending&filters[status][filterType]=set&filters[status][columnKey]=status`
})

const navigation = computed(() => {
  return [
    {
      title: 'Quote Cases Slim',
      icon: ElIconChatLineSquare,
      visible: true,
      children: [
        {
          title: 'Quotes Sold Slim',
          href: `/quote-cases/sold-quote-list-slim${defaultSlimFiltersListQuotes.value}`,
          visible: true,
          icon: ElIconList,
        },
        {
          title: 'Quotes PreSold Slim',
          href: `/quote-cases/presold-quote-list-slim${defaultSlimFiltersListQuotes.value}`,
          visible: true,
          icon: ElIconList,
        },
      ],
    },
    {
      title: 'Quote Data',
      icon: ElIconChatLineSquare,
      visible: true,
      children: [
        {
          title: 'List Quotes Sold',
          href: `/quote-cases/sold-quote-list${defaultFiltersListQuotes.value}`,
          visible: true,
          icon: ElIconList,
        },
        {
          title: 'List Quotes PreSold',
          href: `/quote-cases/presold-quote-list${defaultFiltersListQuotes.value}`,
          visible: true,
          icon: ElIconList,
        },
        {
          title: 'Dead Tomorrow',
          href: `/quote-cases/dead-tomorrow${defaultFiltersDeadTomorrowQuotes.value}`,
          visible: true,
          icon: ElIconList,
        },
        {
          title: 'Dead Cases/Reason',
          href: `/quote-cases/dead-cases-reason${defaultFiltersDeadQuotes.value}`,
          visible: true,
          icon: ElIconList,
        },
        { title: 'Call Logs', href: `/quote-cases/call-logs${defaultCallLogFilters.value}`, visible: true, icon: ElIconPhone },
        { title: 'Call Status', href: `/quote-cases/call-status${defaultCallStatusFilters.value}`, visible: true, icon: ElIconPhoneFilled },
        { title: 'Date Logs', href: `/quote-cases/logs`, visible: true, icon: ElIconCalendar },
      ],
    },
    { title: 'Give a Quote', href: '/quote-cases/create', visible: true, icon: ElIconChatLineRound },
    {
      title: 'Sale Ready Pack',
      icon: ElIconDocumentCopy,
      visible: false,
      children: [
        { title: 'List Packs', href: '/sale-ready-pack', visible: true, icon: ElIconList },
        { title: 'New Demo Pack', href: '/sale-ready-pack/create', visible: true, icon: ElIconDocumentAdd },
      ],
    },
    {
      title: 'Manual Quote',
      icon: ElIconChatLineSquare,
      visible: true,
      children: [
        {
          title: `Sold Quotes ${getPendingSoldQuotesBadge.value} ${getInDraftSoldQuotesBadge.value}`,
          href: '/quote-leads/sold',
          visible: true,
          icon: ElIconList,
        },
        {
          title: `PreSold Quotes ${getPendingPreSoldQuotesBadge.value} ${getInDraftPreSoldQuotesBadge.value}`,
          href: '/quote-leads/presold',
          visible: true,
          icon: ElIconDocumentAdd,
        },
        { title: 'Draft Quotes', href: '/quote-cases/drafts', visible: true, icon: ElIconList },
      ],
    },
    {
      title: 'Reports',
      icon: ElIconFiles,
      visible: false,
      children: [
        {
          title: `Completion Report`,
          href: '/reports/completion-target-report',
          visible: false,
          icon: ElIconPieChart,
        },
        {
          title: `Performance Report`,
          href: '/reports/performance-report',
          visible: false,
          icon: ElIconFlag,
        },
      ],
    },
    {
      title: 'Quote Stats',
      icon: ElIconTrendCharts,
      href: `/quote-cases/stats${defaultFiltersStats.value}`,
      visible: true,
    },
    {
      title: 'Agent Target Stats',
      icon: ElIconTrendCharts,
      href: `/quote-cases/agent-target-stats${defaultFiltersStats.value}&perPage=500`,
      visible: true,
    },
    {
      title: 'Quote Giver Stats',
      icon: ElIconTrendCharts,
      href: '/quote-cases/quote-monthly-giver-stats',
      visible: true,
    },
    {
      title: 'Memorandum of Sale',
      icon: ElIconList,
      href: '/sale-memos',
      visible: true,
    },
    {
      title: 'Replies to Requisitions',
      icon: ElIconList,
      href: '/requisition-replies',
      visible: false,
    },
    {
      title: 'WhatsApp Quotes',
      icon: null,
      img: svgWhatsapp,
      href: '/whatsapp',
      visible: true,
    },
  ]
})

function toggleHoverExpanded(isExpanded) {
  navigationStore.toggleHoverExpanded(isExpanded)
}

function toggleExpanded() {
  navigationStore.toggleExpanded()
}
</script>

<template>
  <aside
    :class="isExpanded ? 'w-64 px-4' : 'w-20 px-3'"
    class="hidden h-full flex-col gap-6 overflow-auto border-r bg-white py-6 text-sm shadow transition-all duration-300 ease-in-out lg:flex"
    @mouseenter="toggleHoverExpanded(true)"
    @mouseleave="toggleHoverExpanded(false)"
  >
    <header class="flex items-center gap-3">
      <NuxtLink to="/">
        <img :src="logo" :class="isExpanded ? 'h-10' : 'mx-auto h-8'" class="!w-auto flex-shrink-0 cursor-pointer" />
      </NuxtLink>

      <el-button
        :icon="navigationStore.expanded ? ElIconCircleClose : ElIconCirclePlus"
        :class="(isExpanded ? '' : 'hidden ') + (navigationStore.expanded ? 'bg-primary text-white' : 'text-primary')"
        class="ml-auto border-primary"
        circle
        @click="toggleExpanded"
      />
    </header>

    <Navigation :navigation />
  </aside>

  <div
    :class="mobileOpen ? 'opacity-50' : 'invisible opacity-0'"
    class="fixed left-0 top-0 z-10 h-full w-full bg-black transition-all duration-300 lg:hidden"
    @click="navigationStore.closeMobile"
  />
  <aside
    :class="mobileOpen ? 'left-0' : '-left-80'"
    class="fixed z-20 flex h-full w-80 flex-col gap-6 overflow-auto border-r bg-white px-4 py-6 text-sm shadow transition-all duration-300 ease-in-out lg:hidden"
  >
    <header class="flex items-center gap-1">
      <NuxtLink to="/">
        <img src="/images/avrillo-logo.png" class="h-10 flex-shrink-0 cursor-pointer" />
      </NuxtLink>

      <ElIcon class="ml-auto text-gray-500" size="20" @click="navigationStore.closeMobile">
        <Close />
      </ElIcon>
    </header>

    <Navigation :navigation />
    <div class="sentry-container">
      <SentryFeedbackButtonHorizontal class="max-w-full whitespace-normal leading-5" :user="user" />
    </div>
  </aside>
</template>
